import { Capacitor } from '@capacitor/core';

// Platform detection utilities
export const isNative = () => Capacitor.isNativePlatform();

export const isWeb = () => Capacitor.getPlatform() === 'web';

export const isIOS = () => Capacitor.getPlatform() === 'ios';

export const isAndroid = () => Capacitor.getPlatform() === 'android';

// Enhanced platform detection for PWA scenarios
export const getPlatformType = () => {
  const isNativePlatform = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform();
  
  if (isNativePlatform) {
    return platform; // 'ios' or 'android'
  }
  
  // Check if running as PWA on iOS or Android
  if (platform === 'web') {
    // Strict PWA detection: Only detect as PWA if display-mode: standalone is true
    // This prevents regular Safari browser from being incorrectly detected as PWA
    const isStandalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroidDevice = /Android/.test(navigator.userAgent);
    
    // Only detect as PWA if display-mode: standalone is explicitly true
    // Removed fallback heuristics (isFullScreen, hasNoAddressBar) that incorrectly
    // detected regular Safari browser as PWA when address bar is hidden
    if (isStandalone && isIOSDevice) {
      return 'ios-pwa';
    }
    
    if (isStandalone && isAndroidDevice) {
      return 'android-pwa';
    }
    
    return 'web';
  }
  
  return 'web';
};

// Check if running as PWA on iOS
export const isIOSPWA = () => getPlatformType() === 'ios-pwa';

// Check if running as standalone PWA (any platform)
export const isStandalonePWA = () => {
  return window.matchMedia('(display-mode: standalone)').matches;
};

// Check if notifications are supported in current environment
export const isNotificationSupported = () => {
  const platformType = getPlatformType();
  
  // Native platforms support notifications
  if (platformType === 'ios' || platformType === 'android') {
    return true;
  }
  
  // Web browsers support notifications
  if (platformType === 'web' && 'Notification' in window) {
    return true;
  }
  
  // PWA on iOS and Android support notifications (like before Capacitor)
  if (platformType === 'ios-pwa' || platformType === 'android-pwa') {
    return 'Notification' in window; // Check if browser supports notifications
  }
  
  return false;
};

// Platform-specific CSS classes
export const getPlatformClass = () => {
  const platformType = getPlatformType();
  if (platformType === 'ios') return 'platform-ios';
  if (platformType === 'android') return 'platform-android';
  if (platformType === 'ios-pwa') return 'platform-ios-pwa';
  if (platformType === 'android-pwa') return 'platform-android-pwa';
  return 'platform-web';
};

// Platform-specific styling utilities
export const getPlatformStyles = () => {
  const platform = Capacitor.getPlatform();
  
  switch (platform) {
    case 'ios':
      return {
        // iOS-specific styles
        primaryColor: '#007AFF',
        secondaryColor: '#5856D6',
        backgroundColor: '#F2F2F7',
        textColor: '#000000',
        borderRadius: '10px',
        shadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      };
    case 'android':
      return {
        // Android-specific styles
        primaryColor: '#007AFF',
        secondaryColor: '#5856D6',
        backgroundColor: '#FAFAFA',
        textColor: '#212121',
        borderRadius: '4px',
        shadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        fontFamily: 'Roboto, "Helvetica Neue", sans-serif'
      };
    default:
      return {
        // Web-specific styles
        primaryColor: '#1976D2',
        secondaryColor: '#42A5F5',
        backgroundColor: '#FFFFFF',
        textColor: '#333333',
        borderRadius: '8px',
        shadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        fontFamily: 'Arial, sans-serif'
      };
  }
};

// Platform-specific component props
export const getPlatformProps = (componentName) => {
  const platform = Capacitor.getPlatform();
  
  switch (componentName) {
    case 'button':
      return platform === 'ios' 
        ? { className: 'ios-button' }
        : platform === 'android'
        ? { className: 'android-button' }
        : { className: 'web-button' };
    
    case 'input':
      return platform === 'ios'
        ? { className: 'ios-input' }
        : platform === 'android'
        ? { className: 'android-input' }
        : { className: 'web-input' };
    
    default:
      return {};
  }
};

/**
 * Normalizes a customer name for display by capitalizing the first letter of each word
 * @param {string} partyName - The customer name to normalize
 * @returns {string} - The normalized customer name
 */
export const normalizePartyNameForDisplay = (partyName) => {
  if (!partyName) return '';
  
  return partyName
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Normalizes a customer name for storage by converting to lowercase
 * @param {string} partyName - The customer name to normalize
 * @returns {string} - The normalized customer name
 */
export const normalizePartyNameForStorage = (partyName) => {
  if (!partyName) return '';
  return partyName.trim().toLowerCase();
}; 